import React, { useState } from "react"
import { funEU } from "../../../utils/firebase"

import Window from "../../../components/structure/window/window"
import WindowCard from "../../../components/ui/window-card/window-card"
import Input from "../../../components/ui/inputs/input"
import Button from "../../../components/ui/button/button"

/**
 * Modal form for adding a new monitored mailbox. Calls the
 * `addMonitoredMailbox` callable in the `functions-communications` codebase
 * (deployed to europe-west2).
 *
 * The display name comes back from Microsoft Graph automatically — we only
 * ask the admin for the email address.
 *
 * On success: the parent's onSnapshot listener picks up the new
 * `monitored_mailboxes` row automatically — no need to refetch manually.
 *
 * On failure: the error is surfaced inline so the admin can correct and retry
 * without losing what they typed. The backend distinguishes between several
 * meaningful failure modes (typo / permission / duplicate / config) so we can
 * give specific guidance for each.
 */
function AddMailbox({ close }) {
	const [email, setEmail] = useState("")
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	const submit = async () => {
		setSaving(true)
		setError(null)

		try {
			const callable = funEU.httpsCallable("addMonitoredMailbox")
			await callable({
				email: email.trim().toLowerCase(),
			})

			close()
		} catch (err) {
			const code = err?.code || ""
			const detail = err?.message || "Unknown error"

			let friendly = detail
			if (code.endsWith("not-found")) {
				friendly = `Microsoft Graph couldn't find a mailbox at that address in this tenant. Check the spelling, and that the mailbox is licensed and enabled.`
			} else if (code.endsWith("already-exists")) {
				friendly = `${email} is already on the monitored list. Check the table — it may be paused.`
			} else if (code.endsWith("failed-precondition")) {
				friendly = `GRAPH_NOTIFICATION_URL is not configured. See docs/communications-setup.md §7.`
			} else if (code.endsWith("permission-denied")) {
				if (detail.includes("Microsoft Graph")) {
					friendly = detail
				} else {
					friendly = `You don't have permission to add mailboxes. Admin role required.`
				}
			}

			setError(friendly)
			setSaving(false)
		}
	}

	const trimmedEmail = email.trim()
	const valid = trimmedEmail.includes("@")

	return (
		<Window
			title="Add monitored mailbox"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className="input-grid cols-1">
					<Input
						type="email"
						placeholder="Email address"
						value={email}
						onChange={setEmail}
					/>

					{error && (
						<div
							className="add-mailbox-error"
							role="alert">
							{error}
						</div>
					)}

					<Button
						label="Add mailbox"
						loading={saving}
						loadingText="Subscribing via Microsoft Graph..."
						onClick={() => submit()}
						disabled={!valid || saving}
					/>
				</div>
			</WindowCard>
		</Window>
	)
}

export default AddMailbox
